import AllocationsAPI from '../../../api/planning/allocations';

export const hasSubAllocations = allocation =>
  Array.isArray(allocation?.sub_allocations) &&
  allocation.sub_allocations.length > 0;

const toLeafAllocation = (sub, parent) => ({
  ...sub,
  is_sub_allocation: true,
  parent_allocation_id: sub.parent_allocation_id || parent.id,
  event_id: sub.event_id || parent.event_id,
  accent_color: sub.accent_color || parent.accent_color,
  status_color: sub.status_color || parent.status_color,
  event_name: sub.event_name || parent.event_name,
  event_label_id: sub.event_label_id || parent.event_label_id,
  event_label_color: sub.event_label_color || parent.event_label_color,
  metadata: {
    ...(parent.metadata || {}),
    ...(sub.metadata || {}),
  },
});

/**
 * Calendar views render one block per booked resource. Parent rows are
 * containers, so nested children are shown on their own resource instead.
 */
export const flattenToLeafAllocations = allocations => {
  const leaves = [];
  const seen = new Set();

  const add = allocation => {
    if (!allocation?.id || seen.has(allocation.id)) return;
    seen.add(allocation.id);
    leaves.push(allocation);
  };

  (allocations || []).forEach(allocation => {
    if (!allocation) return;

    if (hasSubAllocations(allocation)) {
      allocation.sub_allocations.forEach(sub => {
        add(toLeafAllocation(sub, allocation));
      });
      return;
    }

    add(allocation);
  });

  return leaves;
};

/**
 * Collapse sibling slots of the same event on the same resource into one
 * calendar block. The block spans the first start through the last end.
 */
// Collapse leaves to one entry per event (across all resources) — used by the
// month view so all rooms for the same event show as a single bar.
export const collapseLeavesByEvent = leaves => {
  const groups = new Map();

  (leaves || []).forEach(leaf => {
    if (!leaf) return;
    const key = String(leaf.event_id || leaf.id);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(leaf);
  });

  return [...groups.values()].map(slots => {
    const sorted = [...slots].sort(
      (a, b) => new Date(a.start_at) - new Date(b.start_at)
    );
    const first = sorted[0];
    const latestEnd = sorted.reduce((latest, slot) =>
      new Date(slot.end_at) > new Date(latest.end_at) ? slot : latest
    );

    return {
      ...first,
      start_at: first.start_at,
      end_at: latestEnd.end_at,
      title: first.title,
      slot_count: sorted.length,
      grouped_slots: sorted,
    };
  });
};

export const collapseLeavesByEventAndResource = leaves => {
  const groups = new Map();

  (leaves || []).forEach(leaf => {
    if (!leaf) return;
    const groupId = leaf.event_id || leaf.id;
    const key = `${groupId}::${leaf.resource_id}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(leaf);
  });

  return [...groups.values()].map(slots => {
    const sorted = [...slots].sort(
      (a, b) => new Date(a.start_at) - new Date(b.start_at)
    );
    const first = sorted[0];
    const latestEnd = sorted.reduce((latest, slot) =>
      new Date(slot.end_at) > new Date(latest.end_at) ? slot : latest
    );

    return {
      ...first,
      start_at: first.start_at,
      end_at: latestEnd.end_at,
      title: first.title,
      slot_count: sorted.length,
      grouped_slots: sorted,
    };
  });
};

export const resolveParentAllocation = () => null;

export const computeStartTimeShiftMs = (originalStartAt, newStartAt) =>
  new Date(newStartAt).getTime() - new Date(originalStartAt).getTime();

const shiftTimestamp = (isoString, timeShiftMs) =>
  new Date(new Date(isoString).getTime() + timeShiftMs).toISOString();

export const buildShiftedSubAllocationUpdate = (sub, timeShiftMs) => ({
  id: sub.id,
  event_id: sub.event_id,
  resource_id: sub.resource_id,
  start_at: shiftTimestamp(sub.start_at, timeShiftMs),
  end_at: shiftTimestamp(sub.end_at, timeShiftMs),
  buffer_before_min: sub.buffer_before_min,
  buffer_after_min: sub.buffer_after_min,
  title: sub.title,
  description: sub.description,
  metadata: sub.metadata,
});

export const shiftSubAllocationsByDelta = async ({
  store,
  parentId,
  subAllocations,
  timeShiftMs,
}) => {
  if (!timeShiftMs || !subAllocations?.length) return;

  await Promise.all(
    subAllocations.map(async sub => {
      const updateData = buildShiftedSubAllocationUpdate(sub, timeShiftMs);

      store.commit('planningAllocations/UPDATE_SUB_ALLOCATION', {
        parentId,
        subAllocationData: updateData,
      });

      const response = await AllocationsAPI.update(sub.id, {
        event_id: updateData.event_id,
        resource_id: updateData.resource_id,
        start_at: updateData.start_at,
        end_at: updateData.end_at,
        buffer_before_min: updateData.buffer_before_min,
        buffer_after_min: updateData.buffer_after_min,
        title: updateData.title,
        description: updateData.description,
        metadata: updateData.metadata,
      });

      store.commit('planningAllocations/UPDATE_SUB_ALLOCATION', {
        parentId,
        subAllocationData: response.data,
      });
    })
  );
};

export const updateParentToFitChildren = async () => {
  // Parent/child nesting removed; no-op retained for callers during cleanup.
};

export const updateParentAllocationWithChildSync = async ({
  store,
  parentAllocation,
  newStartAt,
  newEndAt,
  resourceId,
  extraFields = {},
}) => {
  if (!parentAllocation?.id) return null;

  const newStartAtIso =
    newStartAt instanceof Date ? newStartAt.toISOString() : newStartAt;
  const newEndAtIso =
    newEndAt instanceof Date ? newEndAt.toISOString() : newEndAt;

  const timeShiftMs = computeStartTimeShiftMs(
    parentAllocation.start_at,
    newStartAtIso
  );

  if (hasSubAllocations(parentAllocation) && timeShiftMs !== 0) {
    await shiftSubAllocationsByDelta({
      store,
      parentId: parentAllocation.id,
      subAllocations: parentAllocation.sub_allocations,
      timeShiftMs,
    });
  }

  const updatedParent = await store.dispatch('planningAllocations/update', {
    id: parentAllocation.id,
    resource_id: resourceId ?? parentAllocation.resource_id,
    start_at: newStartAtIso,
    end_at: newEndAtIso,
    buffer_before_min: parentAllocation.buffer_before_min,
    buffer_after_min: parentAllocation.buffer_after_min,
    title: parentAllocation.title,
    description: parentAllocation.description,
    metadata: parentAllocation.metadata,
    ...extraFields,
  });

  if (hasSubAllocations(parentAllocation)) {
    await updateParentToFitChildren(store, parentAllocation.id);
  }

  return updatedParent;
};
